import { Component, type ComponentInterface, Element, State, h } from '@stencil/core';
import {
  AppleAppStore,
  BBB,
  EqualHousing,
  Facebook,
  GooglePlayStore,
  Instagram,
  TikTok,
  YouTube,
} from '~/components/common/icon/customSVGs';
import { YourPrivacyChoices } from '~/components/wpm-footer/your-privacy-choices';

@Component({
  tag: 'ltc-wpm-footer',
  styleUrl: 'wpm-footer.less',
  shadow: true,
})
export class WpmFooter implements ComponentInterface {
  /**
   * 1. Own Properties
   */

  /**
   * 2. Reference to host HTML element.
   */
  @Element() host: HTMLLtcWpmFooterElement;

  /**
   * 3. State() variables
   */
  @State() showPrivacyChoices = true;

  /**
   * 4. Public Property API
   */

  /**
   * Prop lifecycle events
   */

  /**
   * 5. Events section
   */

  /**
   * 6. Component lifecycle events
   */
  componentWillLoad() {
    window.ketch?.('getConfig', (config: Record<string, unknown>) => {
      if (config.isConfigPaused) {
        this.showPrivacyChoices = false;
      }
    });
  }

  /**
   * 7. Listeners
   */

  /**
   * 8. Public methods API
   */

  /**
   * 9. Local methods
   */
  onKetch = () => {
    window.ketch?.('showExperience', {
      preferenceOptions: {
        tab: 'consentsTab',
        showOverviewTab: true,
        showConsentsTab: true,
        showRightsTab: true,
        showSubscriptionsTab: false,
        supportedCountries: ['US'],
      },
    });
  };

  /**
   * 10. render() function
   */
  render() {
    const theYear = new Date().getFullYear();

    return (
      <div class="container">
        <footer>
          <section class="about-us" tabindex="0">
            <h3>About Us</h3>
            <ul>
              <li>
                <a href="https://press.lendingtree.com/" rel="nofollow">
                  About LendingTree
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/careers/`} rel="nofollow">
                  Careers
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/about/contact-us/`} rel="nofollow">
                  Contact Us
                </a>
              </li>
              <li>
                <a href="https://investors.lendingtree.com/" rel="nofollow">
                  Investors
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/about/partner-with-us/`}>Partner with Us</a>
              </li>
              <li>
                <a href="https://press.lendingtree.com/" rel="nofollow">
                  Press Room
                </a>
              </li>
              <li>
                <a href={`${REPLACE_POWERED_BY_URL}/`} rel="nofollow">
                  Widgets
                </a>
              </li>
            </ul>
          </section>
          <section class="legal" tabindex="0">
            <h3>Legal Information</h3>
            <ul>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/legal/`} rel="nofollow">
                  Overview
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/legal/privacy-policy/`} rel="nofollow">
                  Privacy Policy
                </a>
              </li>
              {this.showPrivacyChoices && (
                <li>
                  <button class="privacy-choices" type="button" onClick={this.onKetch}>
                    Your Privacy Choices <YourPrivacyChoices />
                  </button>
                </li>
              )}
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/legal/state-law-privacy-notice/`} rel="nofollow">
                  State Law Privacy Notice
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/legal/security/`} rel="nofollow">
                  Security
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/legal/advertising-disclosures/`} rel="nofollow">
                  Advertising Disclosures
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/legal/terms-of-use/`} rel="nofollow">
                  Terms of Use
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/legal/licenses-and-disclosures/`} rel="nofollow">
                  Licenses &amp; Disclosures
                </a>
              </li>
              <li>
                <a href={`${REPLACE_SUBS_URL}/`} rel="nofollow">
                  Unsubscribe
                </a>
              </li>
              <li>
                <a href={`${REPLACE_LT_WWW_URL}/legal/security/`} rel="nofollow">
                  Vulnerability Disclosure Program
                </a>
              </li>
              <li class="no-padding">
                <a href={`${REPLACE_LT_WWW_URL}/legal/accessibility-statement/`} rel="nofollow">
                  Accessibility Statement
                </a>
              </li>
            </ul>
          </section>
          <section class="sites" tabindex="0">
            <h3>Other Sites</h3>
            <ul>
              <li>
                <a href="https://www.comparecards.com/" rel="noreferrer" target="_blank">
                  CompareCards
                </a>
              </li>
              <li>
                <a href="https://www.depositaccounts.com/" rel="noreferrer" target="_blank">
                  DepositAccounts
                </a>
              </li>
              <li>
                <a href="https://www.magnifymoney.com/" rel="noreferrer" target="_blank">
                  MagnifyMoney
                </a>
              </li>
              <li>
                <a href="https://quotewizard.com/" rel="noreferrer" target="_blank">
                  QuoteWizard
                </a>
              </li>
              <li>
                <a href="https://snapcap.com/" rel="noreferrer" target="_blank">
                  SnapCap
                </a>
              </li>
              <li class="no-padding">
                <a href="https://www.valuepenguin.com/" rel="noreferrer" target="_blank">
                  ValuePenguin
                </a>
              </li>
            </ul>
          </section>
          <div class="social-apps">
            <section class="socials" tabindex="0">
              <h3>Follow Us</h3>
              <ul class="horizontal-list">
                <li>
                  <a
                    class="social"
                    href="https://www.youtube.com/user/lendingtree"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="LendingTree on YouTube"
                  >
                    <YouTube title="LendingTree on YouTube" />
                  </a>
                </li>
                <li>
                  <a
                    class="social"
                    href="https://www.facebook.com/LendingTree"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="LendingTree on Facebook"
                  >
                    <Facebook title="LendingTree on Facebook" />
                  </a>
                </li>
                <li>
                  <a
                    class="social"
                    href="https://www.instagram.com/LendingTree"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="LendingTree on Instagram"
                  >
                    <Instagram title="LendingTree on Instagram" />
                  </a>
                </li>
                <li>
                  <a
                    class="social"
                    href="https://www.tiktok.com/@lendingtree"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="LendingTree on TikTok"
                  >
                    <TikTok title="LendingTree on TikTok" />
                  </a>
                </li>
              </ul>
            </section>
            <section class="apps" tabindex="0">
              <h3>Download Our App</h3>
              <ul class="horizontal-list">
                <li>
                  <a
                    class="app"
                    href="https://springbylendingtree.onelink.me/24Zy/tt82ktg9"
                    rel="nofollow"
                    aria-label="Download App From Apple App Store"
                  >
                    <AppleAppStore title="Download App From Apple App Store" />
                  </a>
                </li>
                <li>
                  <a
                    class="app"
                    href="https://springbylendingtree.onelink.me/24Zy/tt82ktg9"
                    rel="nofollow"
                    aria-label="Download App From Google Play Store"
                  >
                    <GooglePlayStore title="Download App From Google Play Store" />
                  </a>
                </li>
              </ul>
            </section>
          </div>
          <section class="disclaimer" tabindex="0">
            <div class="law-text">
              LendingTree, LLC is a Marketing Lead Generator and is a Duly Licensed Mortgage Broker, as required by law, with its main
              office located at 1415 Vantage Park Drive, Suite 700, Charlotte, NC 28203 (
              <a href={`${REPLACE_LT_WWW_URL}/tddtty/`} rel="nofollow" aria-label="Information for TDD/TTY users on LendingTree">
                TDD/TTY
              </a>
              ).&#32;
              <a
                href="https://www.nmlsconsumeraccess.org/TuringTestPage.aspx?ReturnUrl=/EntityDetails.aspx/COMPANY/1136"
                target="_blank"
                rel="noreferrer"
                aria-label="View details for NMLS Unique Identifier #1136 on NMLS Consumer Access"
              >
                NMLS Unique Identifier &#35;1136
              </a>
              . LendingTree, LLC is known as LT Technologies in lieu of true name LendingTree, LLC in NY. LendingTree technology and
              processes are patented under U.S. Patent Nos. 6,385,594 and 6,611,816 and licensed under U.S. Patent Nos. 5,995,947 and
              5,758,328. &copy;&nbsp;{theYear}&nbsp;LendingTree,&nbsp;LLC. All Rights Reserved. This site is directed at, and made available
              to, persons in the continental U.S., Alaska and Hawaii only.
            </div>
          </section>
          <section class="security" tabindex="0">
            <span>
              Online Security:&nbsp;
              <a href={`${REPLACE_LT_WWW_URL}/about/onlinesecurity/`} target="_blank" rel="noreferrer">
                Protect Against Fraud
              </a>
            </span>
            <ul class="horizontal-list businesses">
              <li>
                <a class="business" href={`${REPLACE_LT_WWW_URL}/legal/equal-housing-opportunity/`} target="_blank" rel="noreferrer">
                  <EqualHousing title="Equal Housing Opportunity" />
                </a>
              </li>
              <li>
                <a
                  class="business"
                  href="https://www.bbb.org/charlotte/business-reviews/online-loans-referral-services/lendingtree-in-charlotte-nc-109412/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <BBB title="Better Business Bureau" />
                </a>
              </li>
            </ul>
          </section>
        </footer>
      </div>
    );
  }
}
